import { TextField } from "@mui/material";
import React, { useState, useMemo } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import ButtonWithLink from "../../buttons/ButtonWithLink";
import Styles from "./GetInTouch.module.scss";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import { encryptURL } from "../../../helper-fuction/helper";
import { decryptResponse } from "../../../helper-fuction/helper";
import { validateEmail } from "../../../helper-fuction/helper";
import { validatePhoneNumber } from "../../../helper-fuction/helper";
import { WordCount } from "../../../helper-fuction/helper";

import {
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from "@mui/material";
import { capitalizeFirstLetter } from "../../../helper-fuction/helper";
import { appLevelConstants } from "../../../constants/AppLevelConstants";
import { getInTouch } from "../../../service/formService";
import { trackGoogleAdsSubmitLeadFormConversion } from "../../../utils/analytics";

function normalizeProgramProp(programName) {
  if (programName == null) return "NA";
  if (typeof programName === "string") return programName.trim() || "NA";
  if (Array.isArray(programName))
    return String(programName[0] ?? "").trim() || "NA";
  return String(programName).trim() || "NA";
}

/** Build +91xxxxxxxxxx string from local digits; validates length/format. */
function phoneDigitsToSubmitString(digits) {
  const d = String(digits ?? "").replace(/\D/g, "");
  const last10 =
    d.length > 10 && d.startsWith("91") ? d.slice(-10) : d.slice(-10);
  return `+91${last10}`;
}

function validateIndianPhoneDigits(digits) {
  const submit = phoneDigitsToSubmitString(digits);
  const local10 = submit.replace(/^\+91/, "");
  return local10.length === 10 && validatePhoneNumber(submit);
}

/** Split display name into Zoho first/last (legacy behaviour preserved). */
function splitFullNameForZoho(fullName) {
  const trimmed = fullName.trim();
  const count = WordCount(trimmed || " ");
  let space;
  let firstName;
  let lastName;
  if (count < 2) {
    space = trimmed.indexOf(" ");
    firstName = trimmed.substring(space + 1) || trimmed || "-";
    lastName = "------";
  } else {
    space = trimmed.indexOf(" ");
    firstName = trimmed.substring(0, space);
    lastName = trimmed.substring(space + 1);
  }
  return { firstName, lastName };
}

const GetInTouch = ({
  open,
  setDialogOpen,
  sucessToasterGIT,
  errorToasterGIT,
  programName,
  /** When omitted, inferred if program resolves to NA/empty — shows program dropdown like old Contact Us. */
  showProgramDropdown,
  /** When omitted and program dropdown applies, message field is required (old form). */
  requireMessage,
  isDemo,
  isRegister,
  isWorkProfile,
  isExp,
  isSeeYourSelf,
}) => {
  let getContactUsDateTime = new Date().toJSON();
  const leadSource = capitalizeFirstLetter(
    localStorage.getItem("leadSourceName"),
  );

  const programNormalized = normalizeProgramProp(programName);
  const inferredProgramDropdown =
    programNormalized === "NA" || programNormalized === "";

  const showProgramDropdownResolved = useMemo(
    () =>
      showProgramDropdown !== undefined
        ? showProgramDropdown
        : inferredProgramDropdown,
    [showProgramDropdown, inferredProgramDropdown],
  );

  const requireMessageResolved = useMemo(
    () =>
      requireMessage !== undefined ? requireMessage : showProgramDropdownResolved,
    [requireMessage, showProgramDropdownResolved],
  );

  const placeholders = appLevelConstants.forms.getInTouchPlaceholders || {};

  const handleClose = () => {
    setDialogOpen(false);
  };

  const [fullName, setFullName] = useState("");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [email, setEmail] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [preference, setPreference] = useState("");
  const [workProfile, setWorkProfile] = useState("NA");
  const [message, setMessage] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneerror] = useState("");
  const [preferenceError, setPreferenceError] = useState("");
  const [workProfileError, setWorkProfileError] = useState("");
  const [experience, setExperience] = useState("NA");
  const [seeYourself, setSeeYourself] = useState("");
  const [experienceError, setExperienceError] = useState("");
  const [programError, setProgramError] = useState("");
  const [messageError, setMessageError] = useState("");

  const programForSubmission = showProgramDropdownResolved
    ? selectedProgram.trim()
    : programNormalized;

  const submitGetIntouch = async (encryptedUrl) => {
    try {
      const response = await getInTouch(encryptedUrl);
      const encryptedMessage = response.data.data;
      const decryptMessage = decryptResponse(
        encryptedMessage,
        appLevelConstants.REACT_APP_SECRET_KEY,
      );
      setDialogOpen(false);
      trackGoogleAdsSubmitLeadFormConversion();
      sucessToasterGIT(decryptMessage);
    } catch (error) {
      setDialogOpen(false);
      console.log(error);
      errorToasterGIT(error.response?.data?.message ?? "Something went wrong");
    }
  };

  const buildEncryptedSubmitUrl = () => {
    const { firstName, lastName } = splitFullNameForZoho(fullName);
    const phoneSubmit = phoneDigitsToSubmitString(phoneDigits);

    const base =
      "https://www.zohoapis.in/crm/v2/functions/create_il_lead/actions/execute?auth_type=apikey&zapikey=1003.fc9035d274089c149e601eaab0f7bb2c.42d592278f3009bdbe4ac29ee38008f5" +
      "&firstName=" +
      encodeURIComponent(firstName) +
      "&lastName=" +
      encodeURIComponent(lastName) +
      "&fullName=" +
      encodeURIComponent(fullName.trim()) +
      "&email=" +
      encodeURIComponent(email.trim()) +
      "&phone=" +
      encodeURIComponent(phoneSubmit) +
      "&preference=" +
      encodeURIComponent(preference) +
      "&message=" +
      encodeURIComponent(message.trim()) +
      "&leadSource=" +
      encodeURIComponent(leadSource || "") +
      "&currentLeadStatus=" +
      encodeURIComponent(appLevelConstants.forms.getInTouch) +
      "&getContactUsDateTime=" +
      encodeURIComponent(getContactUsDateTime) +
      "&program=" +
      encodeURIComponent(programForSubmission) +
      "&experience=" +
      encodeURIComponent(experience) +
      "&workProfile=" +
      encodeURIComponent(workProfile) +
      "&seeYourself=" +
      encodeURIComponent(seeYourself);

    return encryptURL(base);
  };

  const handleGITSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;

    if (fullName.trim() === "") {
      setFullNameError(appLevelConstants.Errors.fullName);
      hasError = true;
    } else {
      setFullNameError("");
    }

    if (!validateEmail(email)) {
      setEmailError(appLevelConstants.Errors.email);
      hasError = true;
    } else {
      setEmailError("");
    }

    if (!validateIndianPhoneDigits(phoneDigits)) {
      setPhoneerror(appLevelConstants.Errors.phone);
      hasError = true;
    } else {
      setPhoneerror("");
    }

    if (showProgramDropdownResolved && !selectedProgram.trim()) {
      setProgramError(appLevelConstants.Errors.program);
      hasError = true;
    } else {
      setProgramError("");
    }

    if (preference === "") {
      setPreferenceError(appLevelConstants.Errors.preference);
      hasError = true;
    } else {
      setPreferenceError("");
    }

    if (isWorkProfile && (workProfile === "" || workProfile === "NA")) {
      setWorkProfileError(appLevelConstants.Errors.workProfile);
      hasError = true;
    } else {
      setWorkProfileError("");
    }

    if (isExp && experience === "") {
      setExperienceError(appLevelConstants.Errors.experience);
      hasError = true;
    } else {
      setExperienceError("");
    }

    if (requireMessageResolved && message.trim().length === 0) {
      setMessageError(appLevelConstants.Errors.messageRequired);
      hasError = true;
    } else {
      setMessageError("");
    }

    if (!hasError) {
      submitGetIntouch(buildEncryptedSubmitUrl());
    }
  };

  const handlePhoneDigitsChange = (raw) => {
    const numeric = raw.replace(/\D/g, "").slice(0, 10);
    setPhoneDigits(numeric);
  };

  const programOptions =
    appLevelConstants.forms.getInTouchProgramOptions || [];

  const textFieldSx = { marginTop: "10px" };

  return (
    <>
      <div className={Styles["dialog-main-div"]}>
        <Dialog className={Styles["dialog-div"]} open={open}>
          <form id="speak-to-our-counsellor" onSubmit={handleGITSubmit}>
            <DialogTitle
              sx={{ bgcolor: "#F52929", color: "black" }}
              className={Styles["dialog-title"]}
            >
              {appLevelConstants.forms.getInTouchTitle}
            </DialogTitle>

            <DialogContent className={Styles["dialog-content-div"]}>
              <TextField
                autoFocus
                id="fullName"
                name="fullName"
                label="Full Name"
                type="text"
                value={fullName}
                onChange={(ev) => setFullName(ev.target.value)}
                required
                fullWidth
                placeholder={placeholders.fullName}
                sx={textFieldSx}
              />
              {fullNameError && (
                <div style={{ color: "#F52929" }}>{fullNameError}</div>
              )}

              <TextField
                id="phone"
                name="phone"
                label="Phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                value={phoneDigits}
                onChange={(ev) => handlePhoneDigitsChange(ev.target.value)}
                required
                fullWidth
                placeholder={placeholders.phone}
                sx={{ marginTop: "10px", marginBottom: "4px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: "#000",
                          fontSize: "0.95rem",
                          mr: 0.5,
                        }}
                      >
                        +91
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              {phoneError && (
                <div style={{ color: "#F52929" }}>{phoneError}</div>
              )}

              <TextField
                id="email"
                name="email"
                label="Email"
                type="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                required
                fullWidth
                placeholder={placeholders.email}
                sx={{ marginTop: "10px" }}
              />
              {emailError && (
                <div style={{ color: "#F52929" }}>{emailError}</div>
              )}

              {showProgramDropdownResolved && (
                <>
                  <FormControl fullWidth sx={{ marginTop: "14px" }} required>
                    <InputLabel id="program-select-label">
                      {appLevelConstants.forms.programFieldLabel}
                    </InputLabel>
                    <Select
                      labelId="program-select-label"
                      id="program"
                      value={selectedProgram}
                      label={appLevelConstants.forms.programFieldLabel}
                      displayEmpty
                      onChange={(ev) =>
                        setSelectedProgram(String(ev.target.value))
                      }
                    >
                      <MenuItem disabled value="">
                        <em>
                          {appLevelConstants.forms.programSelectPlaceholder}
                        </em>
                      </MenuItem>
                      {programOptions.map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {programError && (
                    <div style={{ color: "#F52929" }}>{programError}</div>
                  )}
                </>
              )}

              <FormLabel
                id="preference-radio-label"
                margin="normal"
                sx={{
                  fontWeight: "bold",
                  color: "black",
                  marginTop: "14px",
                  display: "block",
                }}
              >
                {appLevelConstants.forms.preferenceLabel}
              </FormLabel>

              <RadioGroup
                aria-labelledby="preference-radio-label"
                id="preference"
                name="preference"
                value={preference}
                onChange={(ev) => setPreference(ev.target.value)}
              >
                <FormControlLabel
                  value="Get a call from our counsellor"
                  control={
                    <Radio
                      sx={{
                        color: "#F52929",
                        "&.Mui-checked": {
                          color: "#F52929",
                        },
                      }}
                    />
                  }
                  label="Get a call from our counsellor"
                  className={Styles["radio-button"]}
                />
                <FormControlLabel
                  value="Get program information over email & WA"
                  control={
                    <Radio
                      sx={{
                        color: "#F52929",
                        "&.Mui-checked": {
                          color: "#F52929",
                        },
                      }}
                    />
                  }
                  label="Get program information over email & WA"
                />
                {isRegister && (
                  <FormControlLabel
                    value="Register for the program"
                    control={
                      <Radio
                        sx={{
                          color: "#F52929",
                          "&.Mui-checked": {
                            color: "#F52929",
                          },
                        }}
                      />
                    }
                    label="Register for the program"
                  />
                )}
                {isDemo && (
                  <FormControlLabel
                    value="Get a demo class"
                    control={
                      <Radio
                        sx={{
                          color: "#F52929",
                          "&.Mui-checked": {
                            color: "#F52929",
                          },
                        }}
                      />
                    }
                    label="Get a demo class"
                  />
                )}
              </RadioGroup>
              {preferenceError && (
                <div style={{ color: "#F52929" }}>{preferenceError}</div>
              )}

              {isWorkProfile && (
                <FormLabel
                  id="work-profile-radio-label"
                  margin="normal"
                  sx={{ fontWeight: "bold", color: "black" }}
                >
                  {appLevelConstants.forms.workProfile}
                </FormLabel>
              )}

              {isWorkProfile && (
                <RadioGroup
                  aria-labelledby="work-profile-radio-label"
                  id="workProfile"
                  name="workProfile"
                  value={workProfile}
                  onChange={(ev) => setWorkProfile(ev.target.value)}
                >
                  <FormControlLabel
                    value="Working Professional"
                    control={
                      <Radio
                        sx={{
                          color: "#F52929",
                          "&.Mui-checked": {
                            color: "#F52929",
                          },
                        }}
                      />
                    }
                    label="Working Professional"
                    className={Styles["radio-button"]}
                  />
                  <FormControlLabel
                    value="Entrepreneur"
                    control={
                      <Radio
                        sx={{
                          color: "#F52929",
                          "&.Mui-checked": {
                            color: "#F52929",
                          },
                        }}
                      />
                    }
                    label="Entrepreneur"
                  />
                  <FormControlLabel
                    value="Not working"
                    control={
                      <Radio
                        sx={{
                          color: "#F52929",
                          "&.Mui-checked": {
                            color: "#F52929",
                          },
                        }}
                      />
                    }
                    label="Not working"
                  />
                  <FormControlLabel
                    value="Other"
                    control={
                      <Radio
                        sx={{
                          color: "#F52929",
                          "&.Mui-checked": {
                            color: "#F52929",
                          },
                        }}
                      />
                    }
                    label="Other"
                  />
                </RadioGroup>
              )}

              {workProfileError && isWorkProfile && (
                <div style={{ color: "#F52929" }}>{workProfileError}</div>
              )}

              {isExp && (
                <TextField
                  id="experience"
                  name="experience"
                  label="No. of years of experience"
                  type="text"
                  value={experience !== "NA" ? experience : ""}
                  onChange={(ev) => setExperience(ev.target.value)}
                  fullWidth
                  sx={{ marginTop: "10px" }}
                />
              )}

              {experienceError && isExp && (
                <div style={{ color: "#F52929" }}>{experienceError}</div>
              )}

              {isSeeYourSelf && (
                <TextField
                  id="seeYourself"
                  name="seeYourself"
                  label="Where do you want to see yourself in 3 years?"
                  type="text"
                  value={seeYourself}
                  onChange={(ev) => setSeeYourself(ev.target.value)}
                  fullWidth
                  sx={{ marginTop: "10px" }}
                />
              )}

              <TextField
                margin="dense"
                id="Message"
                label="Anything that you want to let us know"
                name="message"
                value={message}
                onChange={(ev) => setMessage(ev.target.value)}
                type="text"
                fullWidth
                required={requireMessageResolved}
                placeholder={placeholders.message}
                sx={{ color: "black", marginTop: "10px" }}
                multiline
                rows={4}
              />
              {messageError && (
                <div style={{ color: "#F52929" }}>{messageError}</div>
              )}
            </DialogContent>
            <DialogActions className={Styles["dialog-action-button"]}>
              <div>
                <ButtonWithLink name="Submit" type="submit" className={"button"} />
              </div>

              <div onClick={handleClose}>
                <ButtonWithLink name="Cancel" className={"button"} />
              </div>
            </DialogActions>
          </form>
        </Dialog>
      </div>
    </>
  );
};

export default GetInTouch;
